import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractComponent } from 'app/shared/components/abstract-component/abstract.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NavTarget } from '../../shared/components/link/link.component';
import { ListLoader } from '../../shared/components/list-loader/list-loader';
import { EigentumanteilMasterDetailVM } from '../../core/services/eigentum-viewmodel.service';
import { FieldMetaData, PersonGbListEntryMeta, PersonGbMasterDetailEntryMeta } from 'app/core/gb-api';
import { Router } from '@angular/router';
import { ComparatorService } from 'app/shared/services/comparator-service';
import { AppPermissionService } from '../../core/services/app-permission.service';
import { FormatConfigService } from '../../core/services/format-config.service';
import { FormatConfigEntry, PermissionResponse } from '../../core/base-api';
import { LoadingState } from '../../shared/components/list-loader/loading-state';
import { ListLoaderInfinite } from '../../shared/components/list-loader/list-loader-infinite';
import { keysToTranslate } from '../../../i18n/keys-to-translate';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { KompaktGrundstueckComponent } from '../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import {
  KompaktPersonenadresseComponent
} from '../../shared/kompaktdarstellung/personenadresse/kompakt-personenadresse.component';
import { KompaktPersongbComponent } from '../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { ListLoaderWrapperComponent } from '../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { ReadMoreComponent } from '../../shared/widgets/read-more/read-more.component';
import { TranslateNamePipe } from '../../shared/pipes/simple/translate-name.pipe';
import { HistoryDirective } from '../../shared/directives/history.directive';
import { DatumPipe } from '../../shared/pipes/simple/datum.pipe';
import { CapiLoginService } from '../../core/extension/login/capi-login-service';
import { RechtsgrundIcpComponent } from '../shared/component/rechtsgrund-icp/rechtsgrund-icp.component';
import PermissionsEnum = PermissionResponse.PermissionsEnum;
import AuskunftsperreEnum = PersonGbListEntryMeta.AuskunftsperreEnum;

@Component({
  selector: 'cap-eigentum-table-icp',
  templateUrl: './eigentum-table-icp.component.html',
  styleUrls: ['./eigentum-table-icp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    AsyncPipe,
    KompaktGrundstueckComponent,
    KompaktPersonenadresseComponent,
    KompaktPersongbComponent,
    ListLoaderWrapperComponent,
    ReadMoreComponent,
    TranslateNamePipe,
    NgTemplateOutlet,
    HistoryDirective,
    NgClass,
    NgStyle,
    DatumPipe,
    RechtsgrundIcpComponent
  ]
})
export class EigentumTableIcpComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  showAdressen: boolean = true;
  @Input()
  navigateToGrundstueck: NavTarget = NavTarget.NAV_GRUNDSTUECK;
  @Input()
  belegHeader: string = 'DatumOderBelegOderRechtsgrund';
  @Input()
  isEigentuemer: boolean = false;
  @Input()
  verknuepfteEigentuemerIds: string[] = [];

  private _listLoader: ListLoader<EigentumanteilMasterDetailVM>;

  private changeDetectionTimeout: any;
  hasParzellenindex: boolean;

  belegeAnzeigen: boolean = false;
  icpAuthorized: boolean = false;
  isLoggedIn: boolean = false;
  rechtsgrundDatumAnzeigen: boolean = true;

  protected readonly PersonGbListEntryMeta = PersonGbListEntryMeta;
  protected readonly AuskunftsperreEnum = AuskunftsperreEnum;
  protected readonly PersonGbMasterDetailEntryMeta = PersonGbMasterDetailEntryMeta;
  protected readonly FormatConfigEntry = FormatConfigEntry;

  constructor(
    protected router: Router,
    protected appformatConfigService: FormatConfigService,
    private cdr: ChangeDetectorRef,
    private comparatorService: ComparatorService,
    private appPersmissionService: AppPermissionService,
    protected loginService: CapiLoginService
  ) {
    super();
    keysToTranslate<string>(['KeineDatenVorhanden', 'grundstueck.ListTitel', 'PlanZuGrundstueckAnzeigen', 'Kurzbeschreibung']);
  }

  ngOnInit(): void {
    this.activateChangeDetectionTrigger();
    this.addSub(
      this.appformatConfigService.parameterConfig$.subscribe((config) => {
        this.belegeAnzeigen = <any>config?.parameters.BELEGEANZEIGEN;
        this.rechtsgrundDatumAnzeigen = <any>config?.parameters.ERWERBSDATUM_ANZEIGEN;
      })
    );
    this.icpAuthorized = this.appPersmissionService.hasPermission(PermissionsEnum.ICP_AUTHORIZED).valueOf();
    this.isLoggedIn = this.loginService.userLoggedIn;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.changeDetectionTimeout);
  }

  @Input()
  set listLoader(listLoader: ListLoader<EigentumanteilMasterDetailVM>) {
    this._listLoader = listLoader;
    if (listLoader) {
      this.addSub(
        listLoader.items$.subscribe((items) => {
          if (items.length > 0) {
            this.hasParzellenindex = items[0].object.versions[0].mParzellenindex.access !== FieldMetaData.AccessEnum.DEACTIVATED;
            this.addSub(
              listLoader.state$.subscribe((state) => {
                if (state == LoadingState.ALL_DATA_LOADED) {
                  const loader = listLoader as ListLoaderInfinite<EigentumanteilMasterDetailVM>;
                  loader.allItems.sort((a, b) => this.comparatorService.eigentumanteilMasterComparator(a.object, b.object));
                }
              })
            );
          }
        })
      );
    }
  }

  get listLoader(): ListLoader<EigentumanteilMasterDetailVM> {
    return this._listLoader;
  }

  hasAuskunftsperre(personGbHasAuskunftssperre: PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum): AuskunftsperreEnum {
    return personGbHasAuskunftssperre === AuskunftsperreEnum.AUSKUNFT_SPERRE
      ? AuskunftsperreEnum.AUSKUNFT_SPERRE
      : AuskunftsperreEnum.KEINE;
  }

  hasAdresssperre(isAdresseGesperrt: boolean): AuskunftsperreEnum {
    return isAdresseGesperrt && this.appPersmissionService.hasPermission(PermissionsEnum.ADRESSSPERRE)
      ? AuskunftsperreEnum.ADRESS_SPERRE
      : AuskunftsperreEnum.KEINE;
  }

  private activateChangeDetectionTrigger() {
    this.changeDetectionTimeout = setTimeout(() => {
      this.cdr.markForCheck();
      this.activateChangeDetectionTrigger();
    }, 3000);
  }
}
